/**
 * Mock-данные для нового дашборда (шкала оценки 0–10).
 * Отделено от общих моков meerec, чтобы не ломать остальные страницы.
 */

// ─── Менеджеры ────────────────────────────────────────────────────────────

export interface DashManager {
  id: string;
  full: string;
  /** Краткая форма "Фамилия И. О." */
  short: string;
}

export const DASH_MANAGERS: DashManager[] = [
  {id: 'd-1', full: 'Васильева Екатерина Николаевна', short: 'Васильева Е. Н.'},
  {id: 'd-2', full: 'Никитин Павел Юрьевич', short: 'Никитин П. Ю.'},
  {id: 'd-3', full: 'Лебедева Татьяна Сергеевна', short: 'Лебедева Т. С.'},
  {id: 'd-4', full: 'Горохова Виктория Валерьевна', short: 'Горохова В. В.'},
  {id: 'd-5', full: 'Пирожкова Анна Игоревна', short: 'Пирожкова А. И.'},
  {id: 'd-6', full: 'Свиридова Мария Александровна', short: 'Свиридова М. А.'},
  {id: 'd-7', full: 'Кравченко Олег Дмитриевич', short: 'Кравченко О. Д.'},
  {id: 'd-8', full: 'Огурцова Ирина Евгеньевна', short: 'Огурцова И. Е.'},
  {
    id: 'd-9',
    full: 'Зарубашвилли Вахтанг Владимирович',
    short: 'Зарубашвилли В. В.',
  },
  {
    id: 'd-10',
    full: 'Христорождественский Владислав',
    short: 'Христорождественский В.',
  },
  {id: 'd-11', full: 'Меркулов Артём Викторович', short: 'Меркулов А. В.'},
];

/**
 * Маппинг dashboard-id (d-N) → ID реального employee из meerec mock'а.
 * Нужен, чтобы клик по строке открывал существующий ManagerDetailModal.
 * Первые 8 имеют соответствие, для d-9..d-11 возвращает null.
 */
export const dashToEmpId = (dashId: string): string | null => {
  const m = dashId.match(/^d-(\d+)$/);
  if (!m) return null;
  const n = Number(m[1]);
  return n >= 1 && n <= 8 ? `emp-${n}` : null;
};

// ─── Очереди ─────────────────────────────────────────────────────────────

export type QueueTab = 'urgent' | 'management' | 'practices';

export interface DashQueueItem {
  id: string;
  callId: string;
  /** Все строки во вкладке — приоритет 1; внутри 'urgent' это значение задаёт цифру в красном квадрате. */
  priority: 1 | 2 | 3;
  /** Краткое название проблемы. */
  problemTitle: string;
  /** Описание серым. */
  problemDescription: string;
  managerId: string;
  managerShort: string;
  /** Полное ФИО менеджера, для prefill задач. */
  managerFull: string;
  /** Имя клиента (для шапки модалки звонка). */
  clientShort: string;
  /** Балл 0–10 */
  score: number;
  /** Минут назад */
  ageMinutes: number;
  /** Направление стрелки */
  direction: 'incoming' | 'outgoing';
  /** AI-рекомендация конкретного действия. */
  recommendation: string;
  /** Контекст — что именно произошло в звонке. */
  context: string;
  /** Связан ли уже звонок с задачей. */
  taskId: string | null;
  /** Теги/проблемы-ярлыки звонка (для §3 summary). */
  tags: string[];
}

const URGENT_PROBLEMS: {
  title: string;
  desc: string;
  recommendation: string;
  context: string;
  clientShort: string;
  tags: string[];
}[] = [
  {
    title: 'Возможная потеря конверсии',
    desc: 'В звонке выявлена потребность клиента, но не выполнено закрытие на следующий шаг',
    recommendation:
      'Добавить обязательное закрытие на действие после презентации решения. Использовать фразы фиксации договорённости («Когда вам удобно подписать договор?»).',
    context:
      'Клиент Александр интересовался стоимостью заказа, но менеджер не предложил конкретного следующего шага. Разговор завершён без фиксации договорённости.',
    clientShort: 'Александр П.',
    tags: ['Слабое закрытие', 'Нет следующего шага'],
  },
  {
    title: 'Риск потери тёплого лида',
    desc: 'Клиент проявил интерес, но менеджер не зафиксировал договорённость о повторном контакте',
    recommendation:
      'При обнаружении прямого интереса немедленно фиксировать дату/канал следующего контакта в CRM и проговаривать вслух с клиентом.',
    context:
      'Клиент дважды повторил «вернусь к вам позже», но менеджер не уточнил, когда именно и как. Лид с высокой вероятностью «остынет» за 2–3 дня.',
    clientShort: 'Сергей В.',
    tags: ['Тёплый лид', 'Нет дедлайна'],
  },
  {
    title: 'Потеря потенциальной сделки',
    desc: 'Возражение «Дорого» осталось без отработки, клиент завершил разговор',
    recommendation:
      'Использовать связку «цена → ценность → социальное доказательство»: уточнить, что именно дорого, привести пример из похожего клиента, предложить альтернативу.',
    context:
      'Клиент озвучил возражение «Дорого по сравнению с конкурентами». Менеджер не уточнил, какие именно сравнения, и не привёл аргументацию по ценности.',
    clientShort: 'ООО «Лидер»',
    tags: ['Дорого', 'Возражение без отработки'],
  },
  {
    title: 'Грубое нарушение скрипта',
    desc: 'Менеджер пропустил этап выявления потребности и сразу перешёл к презентации',
    recommendation:
      'Вернуться к стандартному скрипту: 3 открытых вопроса до начала презентации. Запланировать индивидуальный разбор и контрольный звонок.',
    context:
      'Менеджер сразу после приветствия начал перечислять характеристики продукта. Потребности клиента так и остались невыясненными.',
    clientShort: 'ИП Кузнецов',
    tags: ['Нарушение скрипта', 'Нет выявления'],
  },
  {
    title: 'Слабая аргументация цены',
    desc: 'При обсуждении стоимости менеджер не привёл сравнение ценности и цены',
    recommendation:
      'Подготовить аргументы по структуре «что входит в цену» и использовать их в каждом звонке, где обсуждается стоимость.',
    context:
      'Клиент спросил «почему так дорого». Ответ менеджера: «это рыночная цена». Никаких пояснений по составу и ценности не было дано.',
    clientShort: 'АО «Прогресс»',
    tags: ['Цена', 'Слабая аргументация'],
  },
  {
    title: 'Незакрытая договорённость',
    desc: 'Не зафиксировано конкретное время следующего шага и канал коммуникации',
    recommendation:
      'В конце каждого звонка проговаривать вслух: дата, время, канал контакта. Закрепить ритуал «3 финальных строки».',
    context:
      'Звонок завершён фразой «созвонимся на неделе». Конкретного дня и канала не зафиксировано — клиент не получит автоматического follow-up.',
    clientShort: 'ООО «Сигма»',
    tags: ['Слабое закрытие'],
  },
  {
    title: 'Эскалация недовольства клиента',
    desc: 'Клиент озвучил претензию по предыдущему контакту, менеджер не отработал ситуацию',
    recommendation:
      'Эскалировать на РОПа в течение 24 часов. Подготовить компенсационное предложение к следующему контакту.',
    context:
      'Клиент сослался на «грубое поведение» в предыдущем звонке. Менеджер не извинился и не предложил решения.',
    clientShort: 'ИП Попов',
    tags: ['Эскалация', 'Жалоба'],
  },
  {
    title: 'Игнорирование сигнала покупки',
    desc: 'Клиент произнёс прямой запрос «давайте оформим», но менеджер продолжил презентацию',
    recommendation:
      'Тренировать распознавание buying signals. При прямом запросе — немедленно переходить к оформлению и закрытию.',
    context:
      'На 4-й минуте клиент сказал «хорошо, давайте оформим». Менеджер продолжил презентацию ещё 3 минуты — клиент потерял интерес.',
    clientShort: 'ООО «Меркурий»',
    tags: ['Buying signal', 'Перепрезентация'],
  },
  {
    title: 'Отсутствие cross-sell предложения',
    desc: 'Сценарий допускал кросс-продажу, но дополнительные позиции не были предложены',
    recommendation:
      'Использовать матрицу cross-sell: к основному продукту X — допы Y и Z. Тренировать естественный переход к допам после основного закрытия.',
    context:
      'Заказ на основной продукт оформлен, но дополнительные SKU (которые покупают 60% похожих клиентов) даже не были озвучены.',
    clientShort: 'АО «Альфа»',
    tags: ['Cross-sell упущен'],
  },
  {
    title: 'Потерянный целевой сегмент',
    desc: 'Профиль клиента совпадает с приоритетным ICP, но звонок закончился без следующего шага',
    recommendation:
      'Передать лид на повышенный приоритет, назначить личный follow-up от РОПа в течение 24 часов.',
    context:
      'Клиент входит в Tier-1 ICP по отрасли и обороту. Звонок завершён без CTA — это упущенная стратегическая сделка.',
    clientShort: 'ООО «Омега»',
    tags: ['Tier-1', 'Нет CTA'],
  },
];

const URGENT_AGES = [5, 10, 15, 22, 35, 48, 65, 90, 120, 180];

// IDs из MOCK_COMMUNICATIONS (C-1001..C-1140) — чтобы клик открывал реальный звонок.
const URGENT_CALL_IDS = [
  'C-1023',
  'C-1045',
  'C-1067',
  'C-1089',
  'C-1101',
  'C-1115',
  'C-1130',
  'C-1042',
  'C-1056',
  'C-1078',
];

export const DASH_QUEUE_URGENT: DashQueueItem[] = URGENT_PROBLEMS.map(
  (p, i) => {
    const mgr = DASH_MANAGERS[i % DASH_MANAGERS.length];
    return {
      id: `qu-${i + 1}`,
      callId: URGENT_CALL_IDS[i],
      priority: 1,
      problemTitle: p.title,
      problemDescription: p.desc,
      managerId: mgr.id,
      managerShort: mgr.short,
      managerFull: mgr.full,
      clientShort: p.clientShort,
      score: Number((2.2 + (i % 5) * 0.35).toFixed(1)),
      ageMinutes: URGENT_AGES[i],
      direction: i % 2 === 0 ? 'incoming' : 'outgoing',
      recommendation: p.recommendation,
      context: p.context,
      // первые 4 строки — без задачи, остальные с задачей (для разнообразия UI)
      taskId: i < 4 ? null : `T-${100 + i}`,
      tags: p.tags,
    };
  },
);

const enrichQueue = (
  rows: Array<
    Omit<
      DashQueueItem,
      | 'managerFull'
      | 'clientShort'
      | 'recommendation'
      | 'context'
      | 'taskId'
      | 'tags'
    >
  >,
): DashQueueItem[] =>
  rows.map((r, i) => {
    const mgr = DASH_MANAGERS.find((m) => m.id === r.managerId);
    return {
      ...r,
      managerFull: mgr?.full ?? r.managerShort,
      clientShort: 'Клиент',
      recommendation:
        'Назначить разбор звонка, согласовать решение с РОПом и зафиксировать договорённость с клиентом.',
      context:
        'Звонок попал в категорию управленческих решений — требуется внимание руководителя для выбора дальнейших шагов.',
      taskId: i % 2 === 0 ? null : `TM-${200 + i}`,
      tags: ['Управленческое решение'],
    };
  });

export const DASH_QUEUE_MANAGEMENT: DashQueueItem[] = enrichQueue([
  {
    id: 'qm-1',
    callId: 'C-1004',
    priority: 2,
    problemTitle: 'Нестандартные условия по договору',
    problemDescription:
      'Клиент запросил отсрочку платежа сверх лимита менеджера — нужно согласование РОПа',
    managerId: 'd-2',
    managerShort: 'Никитин П. Ю.',
    score: 4.1,
    ageMinutes: 45,
    direction: 'outgoing',
  },
  {
    id: 'qm-2',
    callId: 'C-1012',
    priority: 2,
    problemTitle: 'Крупный потенциальный клиент',
    problemDescription:
      'Сделка свыше 800k — рекомендовано личное участие руководителя на следующей встрече',
    managerId: 'd-4',
    managerShort: 'Горохова В. В.',
    score: 4.4,
    ageMinutes: 110,
    direction: 'outgoing',
  },
  {
    id: 'qm-3',
    callId: 'C-1020',
    priority: 2,
    problemTitle: 'Запрос на скидку выше лимита',
    problemDescription:
      'Клиент настаивает на скидке 15% — решение нужно согласовать с РОПом до следующего контакта',
    managerId: 'd-5',
    managerShort: 'Пирожкова А. И.',
    score: 4.0,
    ageMinutes: 180,
    direction: 'outgoing',
  },
  {
    id: 'qm-4',
    callId: 'C-1033',
    priority: 3,
    problemTitle: 'Жалоба на качество предыдущего заказа',
    problemDescription:
      'Клиент озвучил претензию по предыдущей поставке — нужна реакция руководства',
    managerId: 'd-6',
    managerShort: 'Свиридова М. А.',
    score: 3.6,
    ageMinutes: 240,
    direction: 'incoming',
  },
  {
    id: 'qm-5',
    callId: 'C-1051',
    priority: 3,
    problemTitle: 'Запрос пилотного проекта',
    problemDescription:
      'Клиент готов к POC, но условия выходят за рамки стандартного процесса',
    managerId: 'd-3',
    managerShort: 'Лебедева Т. С.',
    score: 4.2,
    ageMinutes: 360,
    direction: 'outgoing',
  },
]);

// ─── Управленческие — детальные кейсы (таб 2 раунд 5 §6.2) ───────────────

export interface ManagementCase {
  id: string;
  priority: 1 | 2 | 3;
  /** Название типа (жирно). */
  typeTitle: string;
  /** Описание (серым). */
  description: string;
  /** «5 менеджеров» или конкретное ФИО. */
  affected: string;
  ageMinutes: number;
  recommendation: string;
}

export const DASH_MANAGEMENT_CASES: ManagementCase[] = [
  {
    id: 'mc-1',
    priority: 1,
    typeTitle: 'Возражение без скрипта',
    description:
      'За неделю — 8 звонков с возражением «Дорого, у конкурента дешевле». Скрипт ответа на это возражение не назначен. Все 8 звонков завершились без сделки.',
    affected: '5 менеджеров',
    ageMinutes: 60,
    recommendation:
      'Создать в скрипте блок «Дорого, у конкурента дешевле»: сравнение по TCO + гарантии + эксклюзивные SKU. Назначить тренинг команде.',
  },
  {
    id: 'mc-2',
    priority: 1,
    typeTitle: 'Системный сбой записи',
    description:
      'За 3 дня — 12 звонков без записи (ошибка телефонии). Аналитика по этим звонкам недоступна. Метрики команды искажены.',
    affected: '8 менеджеров',
    ageMinutes: 180,
    recommendation:
      'Эскалировать в IT для восстановления записи. Параллельно — ручной разбор затронутых звонков с менеджерами по памяти.',
  },
  {
    id: 'mc-3',
    priority: 2,
    typeTitle: 'Нестандартные условия оплаты',
    description:
      '4 крупных клиента запросили отсрочку 60+ дней. Менеджеры не имеют полномочий — все 4 сделки на паузе.',
    affected: '3 менеджера',
    ageMinutes: 480,
    recommendation:
      'Принять решение по политике отсрочки для Tier-1 клиентов. Без явного правила сделки будут «зависать» бессрочно.',
  },
  {
    id: 'mc-4',
    priority: 2,
    typeTitle: 'Разлад с интеграцией CRM',
    description:
      'За неделю — 15 случаев расхождения данных между CRM и аналитикой. Менеджеры дублируют ввод вручную.',
    affected: 'Огурцова И. Е.',
    ageMinutes: 720,
    recommendation:
      'Запустить аудит интеграции в течение 48ч. Пока — назначить ответственного за ручную синхронизацию.',
  },
  {
    id: 'mc-5',
    priority: 1,
    typeTitle: 'Устаревший скрипт работы с возражением',
    description:
      'AI выявил 12 звонков, где клиент высказал возражение «Долгие сроки доставки». Назначенный скрипт устарел (последнее обновление 6 месяцев назад) — ссылается на старую логистическую схему. Конверсия после этого возражения 14% (норма ≥40%).',
    affected: '4 менеджера',
    ageMinutes: 240,
    recommendation:
      'Обновить блок «Долгие сроки доставки» под новую логистику (склад в МСК + 2 региональных хаба). Запустить мини-тренинг — 30 мин, разбор 3 эталонных звонков.',
  },
  {
    id: 'mc-6',
    priority: 1,
    typeTitle: 'Не назначен скрипт для возражения',
    description:
      'За 2 недели — 18 звонков с возражением «Нужна интеграция с 1С». Скрипта ответа на это возражение в системе нет. Менеджеры импровизируют — все 18 звонков завершились без следующего шага.',
    affected: '7 менеджеров',
    ageMinutes: 360,
    recommendation:
      'Сформировать новый скрипт «Интеграция с 1С»: какие коннекторы поддерживаются + кейс клиента похожей отрасли + tech-call с инженером в 24 часа. Эскалировать в product для уточнения roadmap.',
  },
];

// ─── Лучшие практики — детальные кейсы (таб 3 раунд 5 §6.3) ───────────────

export interface PracticeCase {
  id: string;
  /** Название практики (жирно). */
  practiceTitle: string;
  /** Описание (серым). */
  description: string;
  /** ФИО автора. */
  author: string;
  /** Основание (статистика). */
  basis: string;
  /** Прямая цитата из звонка (для раскрытой строки). */
  quote: string;
}

export const DASH_PRACTICE_CASES: PracticeCase[] = [
  {
    id: 'pc-1',
    practiceTitle: 'Техника фиксации договорённости',
    description:
      'После каждого звонка Виктория явно проговаривает следующий шаг с временем и форматом. Конверсия в следующий контакт +18%.',
    author: 'Горохова В. В.',
    basis: '42 звонка за неделю',
    quote:
      'Отлично, тогда зафиксируем: завтра в 12 я пришлю расчёт, в 15 созваниваемся и выбираем — подходит?',
  },
  {
    id: 'pc-2',
    practiceTitle: 'Связка цена → ценность',
    description:
      'Екатерина в ответ на «дорого» всегда даёт развёрнутое сравнение по 3 точкам ценности и приводит кейс похожего клиента. Конверсия после отработки возражения +24%.',
    author: 'Васильева Е. Н.',
    basis: '28 успешных отработок',
    quote:
      'Понимаю про цену. Давайте сравним по трём пунктам: гарантия 36 месяцев против 12 у конкурентов, выезд в день обращения и наша СПб-сервисная сеть. У клиента из похожей отрасли через 3 месяца окупилось за счёт …',
  },
];

export const DASH_QUEUE_PRACTICES: DashQueueItem[] = enrichQueue([
  {
    id: 'qp-1',
    callId: 'C-1062',
    priority: 2,
    problemTitle: 'Образцовая отработка возражения «Дорого»',
    problemDescription:
      'Менеджер чётко связал стоимость и ценность — рекомендуется к разбору на тренинге',
    managerId: 'd-1',
    managerShort: 'Васильева Е. Н.',
    score: 4.9,
    ageMinutes: 90,
    direction: 'outgoing',
  },
  {
    id: 'qp-2',
    callId: 'C-1085',
    priority: 3,
    problemTitle: 'Быстрое закрытие сделки',
    problemDescription:
      'Полный цикл от первого контакта до договорённости — 2 минуты, тиражировать в команде',
    managerId: 'd-2',
    managerShort: 'Никитин П. Ю.',
    score: 4.8,
    ageMinutes: 200,
    direction: 'outgoing',
  },
]);

// ─── KPI «Аналитика за период» ────────────────────────────────────────────

export interface DashKpi {
  /** Средняя оценка звонка, 0–10 */
  avgScore: number;
  avgScoreDelta: number;
  /** Доля звонков с результатом, % */
  withResult: number;
  withResultDelta: number;
  /** Конверсия в результат, % */
  conversion: number;
  conversionDelta: number;
  /** Доля звонков с договорённостью, % */
  withAgreement: number;
  withAgreementDelta: number;
}

export const DASH_KPI: DashKpi = {
  avgScore: 3.0,
  avgScoreDelta: 0.3,
  withResult: 28,
  withResultDelta: 5,
  conversion: 28,
  conversionDelta: 4,
  withAgreement: 28,
  withAgreementDelta: 8,
};

/**
 * KPI по периодам — чтобы PeriodDropdown не был мёртвым.
 * Цифры подобраны декоративно (динамика хуже на коротких периодах).
 */
export const DASH_KPI_BY_PERIOD: Record<string, DashKpi> = {
  day: {
    avgScore: 2.7,
    avgScoreDelta: -0.2,
    withResult: 21,
    withResultDelta: -3,
    conversion: 24,
    conversionDelta: -1,
    withAgreement: 19,
    withAgreementDelta: 2,
  },
  week: DASH_KPI,
  month: {
    avgScore: 3.2,
    avgScoreDelta: 0.4,
    withResult: 31,
    withResultDelta: 6,
    conversion: 32,
    conversionDelta: 5,
    withAgreement: 30,
    withAgreementDelta: 9,
  },
  quarter: {
    avgScore: 3.4,
    avgScoreDelta: 0.6,
    withResult: 34,
    withResultDelta: 8,
    conversion: 36,
    conversionDelta: 7,
    withAgreement: 33,
    withAgreementDelta: 11,
  },
  year: {
    avgScore: 3.5,
    avgScoreDelta: 0.8,
    withResult: 36,
    withResultDelta: 10,
    conversion: 38,
    conversionDelta: 9,
    withAgreement: 35,
    withAgreementDelta: 14,
  },
};

// ─── Рейтинги ─────────────────────────────────────────────────────────────

export type RankChange = number; // > 0 ↑, < 0 ↓, 0 —

export interface RatingRow {
  managerId: string;
  managerFull: string;
  /** Доп. подпись под именем (для оценки — "56 звонков") */
  sub?: string;
  /** Значение метрики */
  value: number;
  /** Изменение позиции к прошлому периоду */
  rankChange: RankChange;
}

const callsWord = (n: number): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'звонок';
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100))
    return 'звонка';
  return 'звонков';
};

const rowsScore: {
  id: string;
  name: string;
  calls: number;
  value: number;
  change: number;
}[] = [
  {
    id: 'd-1',
    name: 'Васильева Екатерина Николаевна',
    calls: 56,
    value: 4.8,
    change: 1,
  },
  {id: 'd-2', name: 'Никитин Павел Юрьевич', calls: 49, value: 4.7, change: -1},
  {
    id: 'd-3',
    name: 'Лебедева Татьяна Сергеевна',
    calls: 44,
    value: 4.6,
    change: -1,
  },
  {
    id: 'd-4',
    name: 'Горохова Виктория Валерьевна',
    calls: 56,
    value: 4.5,
    change: -1,
  },
  {
    id: 'd-5',
    name: 'Пирожкова Анна Игоревна',
    calls: 51,
    value: 4.4,
    change: -1,
  },
  {
    id: 'd-6',
    name: 'Свиридова Мария Александровна',
    calls: 47,
    value: 4.2,
    change: -1,
  },
  {
    id: 'd-7',
    name: 'Кравченко Олег Дмитриевич',
    calls: 53,
    value: 3.8,
    change: 1,
  },
  {
    id: 'd-9',
    name: 'Зарубашвилли Вахтанг Владимирович',
    calls: 41,
    value: 3.7,
    change: 2,
  },
  {
    id: 'd-8',
    name: 'Огурцова Ирина Евгеньевна',
    calls: 48,
    value: 3.6,
    change: 0,
  },
  {
    id: 'd-10',
    name: 'Христорождественский Владислав',
    calls: 39,
    value: 3.5,
    change: 1,
  },
  {
    id: 'd-11',
    name: 'Меркулов Артём Викторович',
    calls: 36,
    value: 3.4,
    change: -2,
  },
];

export const RATING_SCORE: RatingRow[] = rowsScore.map((r) => ({
  managerId: r.id,
  managerFull: r.name,
  sub: `${r.calls} ${callsWord(r.calls)}`,
  value: r.value,
  rankChange: r.change,
}));

export const RATING_CONVERSION: RatingRow[] = [
  {
    managerId: 'd-4',
    managerFull: 'Горохова Виктория Валерьевна',
    value: 91,
    rankChange: 0,
  },
  {
    managerId: 'd-2',
    managerFull: 'Никитин Павел Юрьевич',
    value: 84,
    rankChange: 0,
  },
  {
    managerId: 'd-3',
    managerFull: 'Лебедева Татьяна Сергеевна',
    value: 82,
    rankChange: -1,
  },
  {
    managerId: 'd-1',
    managerFull: 'Васильева Екатерина Николаевна',
    value: 79,
    rankChange: 1,
  },
  {
    managerId: 'd-5',
    managerFull: 'Пирожкова Анна Игоревна',
    value: 76,
    rankChange: -1,
  },
  {
    managerId: 'd-6',
    managerFull: 'Свиридова Мария Александровна',
    value: 68,
    rankChange: 0,
  },
  {
    managerId: 'd-8',
    managerFull: 'Огурцова Ирина Евгеньевна',
    value: 62,
    rankChange: 0,
  },
  {
    managerId: 'd-9',
    managerFull: 'Зарубашвилли Вахтанг Владимирович',
    value: 58,
    rankChange: 2,
  },
  {
    managerId: 'd-10',
    managerFull: 'Христорождественский Владислав',
    value: 56,
    rankChange: 1,
  },
  {
    managerId: 'd-7',
    managerFull: 'Кравченко Олег Дмитриевич',
    value: 53,
    rankChange: 0,
  },
  {
    managerId: 'd-11',
    managerFull: 'Меркулов Артём Викторович',
    value: 47,
    rankChange: -2,
  },
];

// ─── Аналитика по группе ──────────────────────────────────────────────────

export interface GroupAnalyticsRow {
  managerId: string;
  full: string;
  calls: number;
  callsPlan: number;
  successful: number;
  /** Конверсия в целевое действие, % */
  cr: number;
  /** Средняя оценка, 0–10 */
  avgScore: number;
  objectionsIdentified: number;
  /** Процент отработанных возражений, % */
  objectionsHandled: number;
  /** Соответствие скрипту, % */
  script: number;
}

export const GROUP_ANALYTICS: GroupAnalyticsRow[] = [
  {
    managerId: 'd-5',
    full: 'Пирожкова Анна Игоревна',
    calls: 51,
    callsPlan: 60,
    successful: 39,
    cr: 76,
    avgScore: 4.4,
    objectionsIdentified: 47,
    objectionsHandled: 64,
    script: 79,
  },
  {
    managerId: 'd-6',
    full: 'Свиридова Мария Александровна',
    calls: 47,
    callsPlan: 60,
    successful: 32,
    cr: 68,
    avgScore: 4.2,
    objectionsIdentified: 44,
    objectionsHandled: 59,
    script: 75,
  },
  {
    managerId: 'd-7',
    full: 'Кравченко Олег Дмитриевич',
    calls: 53,
    callsPlan: 60,
    successful: 28,
    cr: 53,
    avgScore: 3.8,
    objectionsIdentified: 42,
    objectionsHandled: 47,
    script: 62,
  },
  {
    managerId: 'd-8',
    full: 'Огурцова Ирина Евгеньевна',
    calls: 48,
    callsPlan: 60,
    successful: 30,
    cr: 62,
    avgScore: 3.6,
    objectionsIdentified: 41,
    objectionsHandled: 53,
    script: 65,
  },
  {
    managerId: 'd-10',
    full: 'Христорождественский Владислав',
    calls: 39,
    callsPlan: 60,
    successful: 22,
    cr: 56,
    avgScore: 3.5,
    objectionsIdentified: 35,
    objectionsHandled: 53,
    script: 58,
  },
  {
    managerId: 'd-11',
    full: 'Меркулов Артём Викторович',
    calls: 36,
    callsPlan: 60,
    successful: 17,
    cr: 47,
    avgScore: 3.4,
    objectionsIdentified: 31,
    objectionsHandled: 38,
    script: 55,
  },
  {
    managerId: 'd-1',
    full: 'Васильева Екатерина Николаевна',
    calls: 56,
    callsPlan: 60,
    successful: 44,
    cr: 79,
    avgScore: 4.8,
    objectionsIdentified: 51,
    objectionsHandled: 71,
    script: 86,
  },
  {
    managerId: 'd-2',
    full: 'Никитин Павел Юрьевич',
    calls: 49,
    callsPlan: 60,
    successful: 37,
    cr: 75,
    avgScore: 4.7,
    objectionsIdentified: 45,
    objectionsHandled: 68,
    script: 82,
  },
  {
    managerId: 'd-3',
    full: 'Лебедева Татьяна Сергеевна',
    calls: 44,
    callsPlan: 60,
    successful: 36,
    cr: 82,
    avgScore: 4.6,
    objectionsIdentified: 43,
    objectionsHandled: 66,
    script: 80,
  },
  {
    managerId: 'd-4',
    full: 'Горохова Виктория Валерьевна',
    calls: 56,
    callsPlan: 60,
    successful: 51,
    cr: 91,
    avgScore: 4.5,
    objectionsIdentified: 49,
    objectionsHandled: 72,
    script: 84,
  },
  {
    managerId: 'd-9',
    full: 'Зарубашвилли Вахтанг Владимирович',
    calls: 41,
    callsPlan: 60,
    successful: 24,
    cr: 58,
    avgScore: 3.7,
    objectionsIdentified: 37,
    objectionsHandled: 49,
    script: 60,
  },
];

// ─── Возражения ───────────────────────────────────────────────────────────

export interface ObjectionRow {
  name: string;
  count: number;
  /** Доля по всем упоминаниям, % */
  share: number;
  /** Процент отработки, % */
  handled: number;
}

export const OBJECTIONS_TOTAL_MENTIONS = 260;

export const OBJECTIONS: ObjectionRow[] = [
  {name: 'Дорого / высокая цена', count: 84, share: 32.4, handled: 42},
  {name: 'Подумаю / посоветуюсь', count: 61, share: 23.5, handled: 38},
  {
    name: 'Уже работаю с другим поставщиком',
    count: 47,
    share: 18.1,
    handled: 51,
  },
  {name: 'Не уверен в качестве', count: 28, share: 10.8, handled: 35},
  {name: 'Сейчас не в приоритете', count: 19, share: 7.3, handled: 21},
  {name: 'Сложные условия оплаты', count: 11, share: 4.2, handled: 18},
  {name: 'Не верю в обещанные сроки', count: 6, share: 2.3, handled: 9},
  {name: 'Прочее', count: 4, share: 1.5, handled: 25},
];

// ─── Причины отказа ──────────────────────────────────────────────────────

export interface RefusalRow {
  name: string;
  count: number;
  share: number;
}

export const REFUSALS_TOTAL_CALLS = 153;

export const REFUSALS: RefusalRow[] = [
  {name: 'Высокая комиссия', count: 72, share: 51.21},
  {name: 'Нет времени / занят на монтаже', count: 31, share: 22.04},
  {name: 'Дорого по сравнению с конкурентами', count: 18, share: 12.85},
  {name: 'Уже работаю с другим поставщиком', count: 11, share: 7.84},
  {name: 'Думаю / посоветуюсь', count: 7, share: 4.95},
  {name: 'Не уверен в качестве материалов', count: 5, share: 3.55},
  {name: 'Не сейчас, отложу решение', count: 4, share: 2.84},
  {name: 'Сложные условия доставки', count: 3, share: 2.13},
  {name: 'Нет потребности', count: 2, share: 1.42},
];

// ─── Динамика звонков по дням ────────────────────────────────────────────

export interface CallsDay {
  label: string;
  date: string;
  value: number;
}

export const CALLS_DYNAMICS: CallsDay[] = [
  {label: 'Пн', date: '2026-07-27', value: 38},
  {label: 'Вт', date: '2026-07-28', value: 44},
  {label: 'Ср', date: '2026-07-29', value: 41},
  {label: 'Чт', date: '2026-07-30', value: 49},
  {label: 'Пт', date: '2026-07-31', value: 52},
  {label: 'Сб', date: '2026-08-01', value: 18},
  {label: 'Вс', date: '2026-08-02', value: 9},
  {label: 'Пн', date: '2026-08-03', value: 47},
  {label: 'Вт', date: '2026-08-04', value: 50},
  {label: 'Ср', date: '2026-08-05', value: 46},
  {label: 'Чт', date: '2026-08-06', value: 53},
  {label: 'Пт', date: '2026-08-07', value: 58},
  {label: 'Сб', date: '2026-08-08', value: 22},
];

// ─── Распределение по баллам ──────────────────────────────────────────────

export interface ScoreBucket {
  range: string;
  count: number;
  color: 'green' | 'lime' | 'orange' | 'red-orange' | 'red';
}

export const SCORE_DISTRIBUTION: ScoreBucket[] = [
  {range: '4.5 — 5.0', count: 42, color: 'green'},
  {range: '3.5 — 4.5', count: 156, color: 'lime'},
  {range: '2.5 — 3.5', count: 184, color: 'orange'},
  {range: '1.5 — 2.5', count: 78, color: 'red-orange'},
  {range: '< 1.5', count: 27, color: 'red'},
];

// ─── Период (для dropdown) ────────────────────────────────────────────────

export const DASH_PERIOD_LABELS: Record<string, string> = {
  day: 'Сегодня',
  week: 'Эта неделя',
  month: 'Этот месяц',
  quarter: 'Этот квартал',
  year: 'Этот год',
};
